'use client'

import { useCallback, useMemo, useSyncExternalStore } from 'react'

import { useAuth } from '~/hooks/useAuth'

import {
  getInternalAdminToolsPreferences,
  isInternalAdminToolsEnabled,
  setInternalAdminToolsEnabled,
  subscribeToInternalAdminTools,
  type InternalAdminToolsPreferences
} from './internalAdminTools'

const SERVER_PREFERENCES: InternalAdminToolsPreferences = {}

/** The store is browser-only; SSR renders the hidden state. */
function getServerSnapshot(): InternalAdminToolsPreferences {
  return SERVER_PREFERENCES
}

export type UseInternalAdminToolsResult = {
  /** Whether the signed-in user has internal admin surfaces switched on. */
  enabled: boolean
  /** False until the signed-in user is known; `enabled` is a placeholder until then. */
  isReady: boolean
  setEnabled: (enabled: boolean) => void
  toggle: () => void
}

/**
 * Read and flip the signed-in user's internal-admin-tools preference; every
 * subscriber re-renders on a change, so the tab strip, profile menu, and
 * palette command stay in step. Writes are no-ops until the user is known.
 */
export default function useInternalAdminTools(): UseInternalAdminToolsResult {
  const { user } = useAuth()
  const userId = user?.id
  const preferences = useSyncExternalStore(
    subscribeToInternalAdminTools,
    getInternalAdminToolsPreferences,
    getServerSnapshot
  )
  const enabled = isInternalAdminToolsEnabled(preferences, userId)

  const setEnabled = useCallback(
    (next: boolean) => {
      if (userId) setInternalAdminToolsEnabled(userId, next)
    },
    [userId]
  )
  const toggle = useCallback(() => setEnabled(!enabled), [setEnabled, enabled])

  return useMemo(() => ({ enabled, isReady: !!userId, setEnabled, toggle }), [enabled, userId, setEnabled, toggle])
}
